/**
 * Badges — définitions et attribution (inspiré de GameTime/badgesManager, réduit
 * au strict pédagogique). Les conditions sont évaluées à partir des stats :
 * aucune donnée nominative, aucun classement entre élèves.
 */
import { ref, get, update } from "firebase/database";
import { db } from "../firebase";
import type { StudentStats } from "./progressService";

export interface BadgeDef {
  id: string;
  icon: string;
  nameKey: string;
  descKey: string;
  color: string;
  check: (s: StudentStats) => boolean;
}

export const BADGES: BadgeDef[] = [
  { id: "first_exercise", icon: "✏️", nameKey: "badges.firstExercise", descKey: "badges.firstExerciseDesc", color: "from-blue-500 to-cyan-500", check: (s) => s.totalExercises >= 1 },
  { id: "exercises_25", icon: "💪", nameKey: "badges.exercises25", descKey: "badges.exercises25Desc", color: "from-indigo-500 to-blue-500", check: (s) => s.totalCorrect >= 25 },
  { id: "exercises_100", icon: "🏅", nameKey: "badges.exercises100", descKey: "badges.exercises100Desc", color: "from-amber-400 to-orange-500", check: (s) => s.totalCorrect >= 100 },
  { id: "streak_3", icon: "🔥", nameKey: "badges.streak3", descKey: "badges.streak3Desc", color: "from-orange-500 to-red-500", check: (s) => s.streak.best >= 3 },
  { id: "streak_7", icon: "⚡", nameKey: "badges.streak7", descKey: "badges.streak7Desc", color: "from-yellow-400 to-orange-500", check: (s) => s.streak.best >= 7 },
  { id: "first_test", icon: "📝", nameKey: "badges.firstTest", descKey: "badges.firstTestDesc", color: "from-emerald-500 to-teal-500", check: (s) => s.totalTests >= 1 },
  { id: "tests_10", icon: "🎓", nameKey: "badges.tests10", descKey: "badges.tests10Desc", color: "from-purple-500 to-pink-500", check: (s) => s.totalTests >= 10 },
  { id: "chapter_mastered", icon: "🏆", nameKey: "badges.chapterMastered", descKey: "badges.chapterMasteredDesc", color: "from-yellow-400 to-amber-600", check: (s) => s.chaptersMastered >= 1 },
  { id: "level_5", icon: "🌟", nameKey: "badges.level5", descKey: "badges.level5Desc", color: "from-cyan-400 to-blue-600", check: (s) => s.level >= 5 },
];

/** Évalue et enregistre les nouveaux badges. Renvoie ceux qui viennent d'être débloqués. */
export async function checkBadges(uid: string, stats: StudentStats): Promise<BadgeDef[]> {
  const r = ref(db, `users/${uid}/badges`);
  const snap = await get(r);
  const owned = (snap.val() || {}) as Record<string, { unlockedAt: number }>;
  const fresh = BADGES.filter((b) => !owned[b.id] && b.check(stats));
  if (fresh.length) {
    const updates: Record<string, { unlockedAt: number }> = {};
    fresh.forEach((b) => (updates[b.id] = { unlockedAt: Date.now() }));
    await update(r, updates);
  }
  return fresh;
}

export async function getOwnedBadges(uid: string): Promise<Record<string, { unlockedAt: number }>> {
  const snap = await get(ref(db, `users/${uid}/badges`));
  return (snap.val() || {}) as Record<string, { unlockedAt: number }>;
}
